import { computed, inject, Injectable, signal } from '@angular/core';
import { Crop, Field, FieldActivity, Parcel, Product } from 'src/app/common/_models';
import { BehaviorSubject, firstValueFrom, forkJoin, of } from 'rxjs';
import { ProductService } from 'src/app/common/_services';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../../environments/environment';
import { RegenerativePath } from '../../../../common/models/regenerative-path.model';
import { RegenerativePlotType } from '../../../../common/models/regenerative.model';
import {
  ActivitiesDataStatus,
  ActivitiesGroupType,
  ActivitiesTableFilters,
  ActivityGroupDataScore,
  ActivityGroupDataStatus,
  CropActivityDeclaration,
  RegenerativeDefendingActivities,
  RegenerativeFeedingActivities,
  RegenerativeGroundProcessingActivities,
  RegenerativeHarvestingActivities,
  RegenerativeSeedingActivities,
  SingleActivityDataStatus,
  SingleCropActivityDataScore,
  SingleCropActivityDataStatus,
  SinglePlotActivityDataStatus,
} from '../common-models/activities-table.model';

export type PlotInPath = { type: 'field'; item: Field } | { type: 'parcel'; item: Parcel };

// Define the type of the state of the insert activities stepper
export interface ActivitiesSelectionState {
  currentYear: number;
  plotIdExpanded: string;
  filtersApplied: ActivitiesTableFilters;
  currentPage: number;
}
export interface ActivitiesCompletionState {
  year: number;
  completed: boolean;
}

@Injectable()
export class ActivitiesTableService {
  private regenerativeService = environment.regenerativeServiceUrl;

  //* States Variables
  //Activities Selection State
  private _initialActivitiesSelectionState: ActivitiesSelectionState = {
    currentYear: null,
    plotIdExpanded: null,
    filtersApplied: null,
    currentPage: null,
  };

  private _activitiesSelectionState = signal<ActivitiesSelectionState>(
    this._initialActivitiesSelectionState
  );
  readonly activitiesSelectionState = computed(() => this._activitiesSelectionState());

  //Activities Declaration State
  private _activitiesDeclarationState = signal<CropActivityDeclaration[]>([]);
  readonly activitiesDeclarationState = computed(() => this._activitiesDeclarationState());

  //Activities Completion State
  private _activitiesCompletionState = signal<ActivitiesCompletionState[]>(null); // Initially null
  readonly activitiesCompletionState = computed(() => this._activitiesCompletionState());

  //* Activities Types
  seedingActivities = RegenerativeSeedingActivities;
  defendingActivities = RegenerativeDefendingActivities;
  feedingActivities = RegenerativeFeedingActivities;
  harvestingActivities = RegenerativeHarvestingActivities;
  groundProcessingActivities = RegenerativeGroundProcessingActivities;

  allActivitiesTypes: string[] = [];

  //* Activities Status-Score Data Mapping
  statusToScore = new Map([
    [`${ActivitiesDataStatus.MISSING}`, 0],
    [`${ActivitiesDataStatus.PARTIAL}`, 1],
    [`${ActivitiesDataStatus.COMPLETED}`, 2],
    [`${ActivitiesDataStatus.NOTEXECUTED}`, 3],
  ]);

  //* External Services
  productList: BehaviorSubject<Product[]> = new BehaviorSubject<Product[]>(null);
  http = inject(HttpClient);
  productService = inject(ProductService);

  constructor() {
    const allActivityTypes = [
      ...this.seedingActivities,
      ...this.defendingActivities,
      ...this.feedingActivities,
      ...this.harvestingActivities,
      ...this.groundProcessingActivities,
    ];

    // Assign only unique items to this.allActivitiesTypes
    this.allActivitiesTypes = allActivityTypes.filter((item, index) => {
      return allActivityTypes.indexOf(item) === index;
    });
  }

  //* State Management methods
  // ActivitiesSelectionState
  getActivitiesSelectionState(): ActivitiesSelectionState {
    return this._activitiesSelectionState();
  }

  setActivitiesSelectionState(partialState: Partial<ActivitiesSelectionState>): void {
    this._activitiesSelectionState.update((current) => ({
      ...current,
      ...partialState,
    }));
  }

  resetActivitiesSelectionState(): void {
    this._activitiesSelectionState.set(this._initialActivitiesSelectionState);
  }

  // ActivitiesDeclarationState
  getActivitiesDeclarationState(): CropActivityDeclaration[] {
    return this._activitiesDeclarationState();
  }

  setActivitiesDeclarationState(activitiesDeclarationState: CropActivityDeclaration[]): void {
    this._activitiesDeclarationState.set(activitiesDeclarationState);
  }

  resetActivitiesDeclarationState(): void {
    this._activitiesDeclarationState.set([]);
  }

  // Validation Activities State
  getActivitiesCompletionState(): ActivitiesCompletionState[] {
    return this._activitiesCompletionState();
  }

  resetActivitiesCompletionState(): void {
    this._activitiesCompletionState.set(null); // Reset the validation state
  }

  setActivitiesCompletionState(validation: ActivitiesCompletionState[]): void {
    this._activitiesCompletionState.set(validation); // Update the validation state
  }

  //* API Methods
  /**
   * Retrieves the list of CROP activity declarations for a given regenerative path ID.
   *
   * @param {string} pathId - The ID of the regenerative path to fetch declarations for.
   * @returns {Promise<{ data: CropActivityDeclaration[]; totalCount: number }>}
   * An object containing the list of crop activity declarations and their total count.
   *
   *? NB the method name is a bit mesleading due to the name of the API endpoint
   *? The declarationObject is actually one for single crop
   */
  async getFieldActivityDeclarations(
    pathId: string
  ): Promise<{ data: CropActivityDeclaration[]; totalCount: number }> {
    const response = await firstValueFrom(
      this.http.get<{ data: CropActivityDeclaration[]; totalCount: number }>(
        `${this.regenerativeService}api/private/v1/paths/fieldActivityDeclarations/${pathId}`
      )
    );
    return response;
  }

  /**
   * Updates the field activity declarations for a given regenerative path.
   * Splits the declarations into ones to create (without `id`) and ones to update (with `id`),
   * then performs the corresponding HTTP requests and returns the combined result.
   *
   * @param {CropActivityDeclaration[]} activitiesDeclarationState - The list of crop activity declarations, either new or existing.
   * @param {string} pathId - The ID of the regenerative path to which the declarations belong.
   * @returns {Promise<{ data: CropActivityDeclaration[]; totalCount: number }>}
   * An object containing all created and updated crop activity declarations and their total count.
   */
  async updateFieldActivityDeclaration(
    activitiesDeclarationState: CropActivityDeclaration[],
    pathId: string
  ): Promise<{ data: CropActivityDeclaration[]; totalCount: number }> {
    const toCreate: CropActivityDeclaration[] = [];
    const toUpdate: CropActivityDeclaration[] = [];

    // Split the array based on presence of `id`
    for (const declaration of activitiesDeclarationState) {
      if (declaration.id) {
        toUpdate.push(declaration);
      } else {
        toCreate.push(declaration);
      }
    }

    // Prepare HTTP observables
    const create$ = toCreate.length
      ? this.http.post<{ data: CropActivityDeclaration[]; totalCount: number }>(
          `${this.regenerativeService}api/private/v1/paths/fieldActivityDeclarations/${pathId}`,
          toCreate
        )
      : of({ data: [] });

    const update$ = toUpdate.length
      ? this.http.put<{ data: CropActivityDeclaration[]; totalCount: number }>(
          `${this.regenerativeService}api/private/v1/paths/fieldActivityDeclarations/${pathId}`,
          toUpdate
        )
      : of({ data: [] });

    // Execute both calls in parallel
    const [createdResponse, updatedResponse] = await firstValueFrom(forkJoin([create$, update$]));

    const all = [...createdResponse.data, ...updatedResponse.data];
    return { data: all, totalCount: all.length };
  }

  //* Helpers methods
  /**
   * Retrieves the list of plots (fields or parcels) and their associated crops
   * that are included within a given regenerative path.
   *
   * @param regenerativePath - The regenerative path containing sections referencing fields or parcels.
   * @param fields - The available fields to search from.
   * @param parcels - The available parcels to search from.
   * @returns An object with:
   *    - plotsInPath: Map to clearly distinguish between Fields and Parcels that are part of the path.
   *    - cropsInPath: the unique crops found within the plots in the path.
   */
  getPlotsAndCropsInPath(
    regenerativePath: RegenerativePath,
    fields: Field[],
    parcels: Parcel[]
  ): {
    plotsInPath: Map<number, PlotInPath>;
    cropsInPath: Crop[];
  } {
    // Map where key = plot ID, value = { type, item }
    const plotsInPath = new Map<number, PlotInPath>();
    const cropsInPath: Crop[] = [];

    const uniqueCropsId = new Set<number>(); // Tracks unique crop IDs

    if (regenerativePath.sections?.length) {
      regenerativePath.sections.forEach((section) => {
        if (section.parcelId) {
          const parcel = parcels.find((parcel) => parcel.id === section.parcelId);
          if (parcel && !plotsInPath.has(parcel.id)) {
            plotsInPath.set(parcel.id, { type: RegenerativePlotType.PARCEL, item: parcel });
          }
        } else if (section.fieldId) {
          const field = fields.find((field) => field.id === section.fieldId);
          if (field && !plotsInPath.has(field.id)) {
            plotsInPath.set(field.id, { type: RegenerativePlotType.FIELD, item: field });
          }
        }
      });
    }

    // Collect crops from plots
    plotsInPath.forEach(({ item }) => {
      if ('crops' in item && Array.isArray(item.crops)) {
        item.crops.forEach((crop) => {
          if (!uniqueCropsId.has(crop.id)) {
            uniqueCropsId.add(crop.id);
            cropsInPath.push(crop);
          }
        });
      }
    });

    return { plotsInPath, cropsInPath };
  }

  /**
   * Creates table rows for each plot in the given regenerative path, associating
   * fields and parcels with the corresponding crops.
   *
   * This method processes the provided plots, maps them to a `SinglePlotActivityDataStatus`
   * object, and associates the relevant field or parcel to each row. Then it maps each crop to
   * the corresponding table row based on its field or parcel.
   *
   * @param {Map<number, PlotInPath>} plotsInPath - A map of plot IDs to their corresponding plot data in the regenerative path.
   * @param {Field[]} fields - An array of fields available in the regenerative path.
   * @param {Crop[]} cropsInPath - An array of crops within the regenerative path.
   * @returns {SinglePlotActivityDataStatus[]} - An array of `SinglePlotActivityDataStatus` objects representing table rows.
   * Each row contains the field or parcel associated with the plot and its related crops.
   */
  createTableRows(
    plotsInPath: Map<number, PlotInPath>,
    fields: Field[],
    cropsInPath: Crop[]
  ): SinglePlotActivityDataStatus[] {
    const tableRows: SinglePlotActivityDataStatus[] = [];

    plotsInPath.forEach((plot, key) => {
      const newTableRow = new SinglePlotActivityDataStatus();

      newTableRow.id = key;

      if (plot.type === 'field') {
        // TypeScript knows plot.item is Field here
        newTableRow.field = plot.item;
      } else {
        // TypeScript knows plot.item is Parcel here
        const fieldOfParcel = fields.find((field) => field.id === plot.item.field.id);

        if (!fieldOfParcel) {
          console.warn(`Field not found for parcel id ${plot.item.id}`);
          return;
        }

        newTableRow.field = fieldOfParcel;
        newTableRow.parcel = plot.item;
      }

      tableRows.push(newTableRow);
    });

    // Let's add the crops to the tableRows
    tableRows.forEach((row) => {
      row.crops = cropsInPath
        .filter((crop) =>
          row.parcel ? crop.parcel?.id === row.parcel?.id : crop.field?.id === row.field?.id
        )
        .map((crop) => ({
          crop,
          activitiesGroupDStatus: new ActivityGroupDataStatus(),
        }));
    });

    return tableRows;
  }

  /**
   * Maps a list of field activities to their corresponding data status and activity categories.
   *
   * This method processes each activity, checks its type, and verifies if it belongs to the specified crops.
   * It then calculates the status for each activity based on its groupType (e.g., seeding, feeding, harvesting, etc.).
   * Afterward, it returns an array of `SingleActivityDataStatus` objects, representing each activity's field, crop,
   * and data status, along with additional metadata.
   *
   * @param {FieldActivity[]} activities - An array of field activities to be processed.
   * @param {Field[]} fields - An array of fields associated with the activities.
   * @param {Crop[]} crops - An array of crops associated with the activities.
   * @returns {Promise<SingleActivityDataStatus[]>} - A promise that resolves to an array of `SingleActivityDataStatus` objects,
   * each containing activity details, data status, and metadata.
   */
  async mappingActivities(
    activities: FieldActivity[],
    fields: Field[],
    crops: Crop[]
  ): Promise<SingleActivityDataStatus[]> {
    const cropsId = crops.map((crop) => crop.id);
    const allActivitiesDStatus: SingleActivityDataStatus[] = [];

    if (activities?.length) {
      const activityPromises = activities.map(async (activity) => {
        if (
          this.allActivitiesTypes.includes(activity.type) &&
          cropsId.includes(+activity.crop.id)
        ) {
          const listActivityGroupDStatus: {
            activityGroupType: string;
            status: number;
            id: number;
            metadata?: any;
          }[] = [];

          // Collect promises for async status checks
          const statusPromises: Promise<void>[] = [];

          if (this.harvestingActivities.includes(activity.type)) {
            const { status, metadata } = this.checkStatusForHarvesting(activity);
            listActivityGroupDStatus.push({
              activityGroupType: ActivitiesGroupType.HARVESTING,
              status,
              id: activity.id as number,
              metadata,
            });
          }

          if (this.seedingActivities.includes(activity.type)) {
            statusPromises.push(
              this.checkStatusForProduct(activity, 'seeds').then(({ status, metadata }) => {
                listActivityGroupDStatus.push({
                  activityGroupType: ActivitiesGroupType.SEEDING,
                  status: status ?? 0,
                  id: activity.id as number,
                  metadata,
                });
              })
            );
          }

          if (this.defendingActivities.includes(activity.type)) {
            statusPromises.push(
              this.checkStatusForProduct(activity, 'chemicals').then(({ status, metadata }) => {
                listActivityGroupDStatus.push({
                  activityGroupType: ActivitiesGroupType.DEFENDING,
                  status: status ?? 0,
                  id: activity.id as number,
                  metadata,
                });
              })
            );
          }

          if (this.feedingActivities.includes(activity.type)) {
            statusPromises.push(
              this.checkStatusForProduct(activity, 'fertilizers').then(({ status, metadata }) => {
                listActivityGroupDStatus.push({
                  activityGroupType: ActivitiesGroupType.FEEDING,
                  status: status ?? 0,
                  id: activity.id as number,
                  metadata,
                });
              })
            );
          }

          if (this.groundProcessingActivities.includes(activity.type)) {
            listActivityGroupDStatus.push({
              activityGroupType: ActivitiesGroupType.GROUND_PROCESSING,
              status: this.getScore(`${ActivitiesDataStatus.COMPLETED}`),
              id: activity.id as number,
              metadata: {
                activityDate: activity.activityDate,
                specificActivityType: activity.type,
              },
            });
          }

          // Wait for all async status calculations to finish
          await Promise.all(statusPromises);

          if (listActivityGroupDStatus.length) {
            listActivityGroupDStatus.forEach((a) => {
              const foundedField = fields.find((field) => field.id == activity.field.id);
              const foundedCrop = foundedField?.crops.find((crop) => crop.id == activity.crop.id);

              allActivitiesDStatus.push({
                activityId: a.id,
                field: foundedField,
                crop: foundedCrop,
                dataStatus: a.status,
                activityGroupType: a.activityGroupType,
                metadata: a.metadata,
              });
            });
          }
        }
      });

      // Wait for all activities to be processed
      await Promise.all(activityPromises);
    }

    return allActivitiesDStatus;
  }

  /**
   * Groups mapped activities by crop and computes an activity score for each grouptype.
   *
   * For each crop in the provided plots, this method:
   * - Creates a new `SingleCropActivityDataScore` object.
   * - Associates it with the correct field and (if applicable) parcel.
   * - Filters all activities related to the current crop.
   * - Sets a score for each activity group type (seeding, harvesting, defending, feeding, ground processing)
   *   based on the filtered activities.
   *
   * The initial score for each activity group is 0, which corresponds to `MISSING` status.
   *
   * @param {SingleActivityDataStatus[]} mappedActivities - List of activities with their statuses and metadata.
   * @param {Map<number, PlotInPath>} plotsInPath - Map of plot IDs to plot data, including fields or parcels and their crops.
   * @param {Field[]} fields - Array of all fields, used to resolve the field for a given parcel.
   * @returns {SingleCropActivityDataScore[]} - Array of crops each with their corresponding activity scores.
   */
  groupingActivitiesByCrop(
    mappedActivities: SingleActivityDataStatus[],
    plotsInPath: Map<number, PlotInPath>,
    fields: Field[]
  ): SingleCropActivityDataScore[] {
    const cropsWithActivityStatusScore: SingleCropActivityDataScore[] = [];

    plotsInPath.forEach((plot) => {
      plot.item.crops.forEach((crop) => {
        // We initialize a brand new cropObj
        // NB the initial score for each activity category is 0 which corresponds to MISSING
        const cropObj: SingleCropActivityDataScore = new SingleCropActivityDataScore();
        // We assign the Field, the Crop whole object, and the Parcel if it exists
        cropObj.field =
          plot.type === 'field' ? plot.item : fields.find((f) => f.id === plot.item.field.id);
        cropObj.crop = crop;
        cropObj.parcel = plot.type === 'parcel' ? plot.item : null;

        // we retrieve only the activities related to the current crop
        const activitiesOnCurrentCrop = mappedActivities.filter(
          (activity) => activity.crop.id === crop.id
        );

        cropObj.seeding = this.setScoreForCropByActivityGroup(
          crop.id,
          activitiesOnCurrentCrop,
          ActivitiesGroupType.SEEDING
        );
        cropObj.harvesting = this.setScoreForCropByActivityGroup(
          crop.id,
          activitiesOnCurrentCrop,
          ActivitiesGroupType.HARVESTING
        );
        cropObj.defending = this.setScoreForCropByActivityGroup(
          crop.id,
          activitiesOnCurrentCrop,
          ActivitiesGroupType.DEFENDING
        );
        cropObj.feeding = this.setScoreForCropByActivityGroup(
          crop.id,
          activitiesOnCurrentCrop,
          ActivitiesGroupType.FEEDING
        );
        cropObj.ground_processing = this.setScoreForCropByActivityGroup(
          crop.id,
          activitiesOnCurrentCrop,
          ActivitiesGroupType.GROUND_PROCESSING
        );

        cropsWithActivityStatusScore.push(cropObj);
      });
    });

    return cropsWithActivityStatusScore;
  }

  /**
   * Groups activity status values by Plot based on crop data.
   *
   * For each table row (Plot), this computes:
   * - The worst (lowest) status per activity across all crops.
   * - A summary status representing the overall worst status in the Plot.
   *
   * Assumes status values are numerical and higher means better.
   *
   * @param cropsObj - Crops with activity status scores
   * @param tableRows - Rows representing Plots with their crops
   * @returns Table rows with computed activity and summary statuses
   */
  groupingActivitiesByPlot(
    cropsObj: SingleCropActivityDataScore[],
    tableRows: SinglePlotActivityDataStatus[]
  ): SinglePlotActivityDataStatus[] {
    tableRows.forEach((row) => {
      const plotActivityGroupDScore = new ActivityGroupDataScore();

      // Keep track of the overall worst (minimum) status for the current plot
      let plotSummaryDStatus: number = null;

      if (row.crops?.length) {
        row.crops.forEach((crop) => {
          const cropObj = cropsObj.find((cropObj) => cropObj.crop.id === crop.crop.id);

          if (cropObj) {
            // Map numerical status to string for each activity group of the crop
            crop.activitiesGroupDStatus = {
              seeding: this.getStatus(cropObj.seeding),
              harvesting: this.getStatus(cropObj.harvesting),
              defending: this.getStatus(cropObj.defending),
              feeding: this.getStatus(cropObj.feeding),
              ground_processing: this.getStatus(cropObj.ground_processing),
            };

            Object.keys(plotActivityGroupDScore).forEach((key) => {
              const currentValue = cropObj[key];

              if (currentValue !== undefined) {
                // Update plotActivityGroupDScore with the minimum value
                plotActivityGroupDScore[key] =
                  plotActivityGroupDScore[key] != null
                    ? Math.min(plotActivityGroupDScore[key], currentValue)
                    : currentValue;
              } else {
                // Fallback when the activity is missing (currentValue is undefined)
                plotActivityGroupDScore[key] = plotActivityGroupDScore[key] ?? 0;
              }

              // Update the plotSummaryDStatus
              const value = plotActivityGroupDScore[key];
              plotSummaryDStatus =
                plotSummaryDStatus === null ? value : Math.min(plotSummaryDStatus, value);
            });
          }
        });
      }

      // Convert numerical statuses back to string representations
      row.plotActivitiesDStatus = {
        seeding: this.getStatus(plotActivityGroupDScore.seeding),
        harvesting: this.getStatus(plotActivityGroupDScore.harvesting),
        defending: this.getStatus(plotActivityGroupDScore.defending),
        feeding: this.getStatus(plotActivityGroupDScore.feeding),
        ground_processing: this.getStatus(plotActivityGroupDScore.ground_processing),
      };

      // Set the overall summary status for the plot
      row.plotSummaryDStatus = this.getStatus(plotSummaryDStatus ?? 0);
    });

    return tableRows;
  }

  /**
   * Applies a cascade of filters to the provided data table based on the given filter criteria.
   *
   * - Filters by `sectionName`, matching it against the section name of each field.
   * - Filters crops within each item based on their activity group data status.
   * - Excludes any items where the resulting crop list is empty after filtering.
   *
   * @param {SinglePlotActivityDataStatus[]} dataTable - The original unfiltered dataset.
   * @param {any} filters - The filter criteria containing optional `sectionName` and `dataStatus` fields.
   *   - `sectionName`: A string used to match the field's section name (case-insensitive).
   *   - `dataStatus`: An array of status objects (each with an `id`) used to filter crops by activity group status.
   *
   * @returns {SinglePlotActivityDataStatus[]} The filtered dataset with updated crops.
   */
  applyFilters(dataTable: SinglePlotActivityDataStatus[], filters): SinglePlotActivityDataStatus[] {
    if (!dataTable) return [];
    if (!filters) return dataTable;

    let result = dataTable;

    // Filter by Section Name
    if (filters.sectionName) {
      result = result.filter((item) =>
        item.field.name.toLowerCase().includes(filters.sectionName.toLowerCase())
      );
    }

    // Filter by Data Status (crop-level filtering)
    if (filters.dataStatus && filters.dataStatus.length) {
      result = result
        .map((item) => ({
          ...item,
          crops: item.crops.filter((crop) => this.filterCropByActivityStatus(crop, filters)),
        }))
        .filter((item) => item.crops && item.crops.length);
    }

    return result;
  }

  //* Private methods

  /**
   * Calculates the score for a crop's activity group based on its execution status and activity data.
   *
   * - Returns `3` if the activity group is explicitly flagged as not executed.
   * - If executed, returns the lowest `dataStatus` value from the related activities.
   * - If no activities are found and not flagged, defaults to `0`.
   *
   * @param cropId - The ID of the crop to evaluate.
   * @param activitiesOnCurrentCrop - The list of activity path details related to the crop.
   * @param activityGroup - The specific activity group to assess (e.g., seeding, feeding).
   * @returns A score: `3` for not executed, minimum `dataStatus` if activities exist, or `0` by default.
   */
  setScoreForCropByActivityGroup(
    cropId: number,
    activitiesOnCurrentCrop: SingleActivityDataStatus[],
    activityGroup: ActivitiesGroupType
  ): number {
    // Always check if activity is explicitly flagged as not executed first
    const cropActivityDeclaration = this.activitiesDeclarationState().find(
      (declaration) => declaration.cropId === cropId
    );

    const isNotExecuted = cropActivityDeclaration?.activityDeclarations.find(
      (activity) => activity.groupType === activityGroup
    )?.notExecution;

    if (isNotExecuted) {
      return 3; // Not executed
    }

    // Then check for specific activities and calculate the score
    const specificActivities = activitiesOnCurrentCrop.filter(
      (activity) => activity.activityGroupType === activityGroup
    );

    if (specificActivities.length) {
      return specificActivities.reduce(
        (min, curr) => Math.min(min, curr.dataStatus),
        specificActivities[0].dataStatus
      );
    }

    // If no activity and not flagged, default score
    return 0;
  }

  // Checks whether an Harvesting activity has all the necessary information
  checkStatusForHarvesting(activity: FieldActivity): { status: number; metadata: any } {
    let dataStatus: number = null;

    let yieldValue: number = null;
    //? fieldActivityItems is an array of objects that contains the quantity and the unit of the harvested product
    if (!activity.fieldActivityItems || !activity.fieldActivityItems.length) {
      dataStatus = this.getScore(`${ActivitiesDataStatus.PARTIAL}`);
    } else {
      let allInfoHarvesting: boolean = true;
      activity.fieldActivityItems.forEach((item) => {
        if (!item.quantity || !item.unit) {
          allInfoHarvesting = false;
        } else {
          let harvestedQuantity: number = null;

          // Convert the quantity to kg
          switch (item.unit) {
            case 'q': // quintal
              harvestedQuantity = item.quantity * 100;
              break;

            case 't': // ton
              harvestedQuantity = item.quantity * 1000;
              break;

            case 'kg': // kilogram
            default:
              harvestedQuantity = item.quantity;
              break;
          }
          yieldValue += harvestedQuantity;
        }
      });
      // Check residuals have NOT been specified by the user
      if (!activity.cropResiduesDestination) {
        allInfoHarvesting = false;
      }

      if (allInfoHarvesting) {
        dataStatus = this.getScore(`${ActivitiesDataStatus.COMPLETED}`);
      } else {
        dataStatus = this.getScore(`${ActivitiesDataStatus.PARTIAL}`);
      }
    }

    return {
      status: dataStatus,
      metadata: {
        activityDate: activity.activityDate,
        yield: yieldValue,
        residuals: activity.cropResiduesDestination,
      },
    };
  }

  // Checks whether the Activities have all the info about the products used
  private async checkStatusForProduct(
    activity: FieldActivity,
    productType: string
  ): Promise<{ status: number; metadata: any }> {
    // Default to PARTIAL
    let dataStatus: number = this.getScore(`${ActivitiesDataStatus.PARTIAL}`);

    if (!activity.mixes?.length) {
      return {
        status: dataStatus,
        metadata: {
          activityDate: activity.activityDate,
          productsUsed: null,
        },
      };
    }

    const validProducts: { productName: string; dose: number; doseUnit: string }[] = [];

    for (const mix of activity.mixes) {
      if (!mix.product?.id) {
        continue; // Skip if no product is associated
      }

      // Await the asynchronous product check
      const { isPertinent, productName } = await this.checkIsProductPertinent(
        productType,
        mix.product.id as number
      );

      if (isPertinent) {
        validProducts.push({
          productName,
          dose: mix.dose,
          doseUnit: mix.doseUnit,
        });
      }
    }
    // If we have valid products and all have dose and doseUnit filled, mark the activity as COMPLETED
    if (validProducts.length && validProducts.every((p) => p.dose && p.doseUnit)) {
      dataStatus = this.getScore(`${ActivitiesDataStatus.COMPLETED}`);
    }

    return {
      status: dataStatus,
      metadata: {
        activityDate: activity.activityDate,
        productsUsed: validProducts,
      },
    };
  }

  // Checks whether the product used in the activity is of the right type (e.g. Defending activity wants a Chemicals product)
  private async checkIsProductPertinent(
    acceptedProductType: string,
    productId: number
  ): Promise<{ isPertinent: boolean; productName: string | null }> {
    const foundProduct = this.productList.value.find((p) => p.id === productId);
    if (foundProduct)
      return {
        isPertinent: foundProduct.type === acceptedProductType,
        productName: foundProduct.name,
      };

    try {
      const product = await firstValueFrom(this.productService.getProduct(productId));

      return {
        isPertinent: product?.type === acceptedProductType,
        productName: foundProduct.name,
      };
    } catch {
      return {
        isPertinent: false,
        productName: null,
      };
    }
  }

  // Function to get the score as Number from the status as String
  private getStatus(score: number): string | null {
    // Convert the Map entries to an array and loop through it
    const keyValueArray = Array.from(this.statusToScore.entries());

    for (const [status, mappedScore] of keyValueArray) {
      if (mappedScore === score) {
        // Return the status if a match is found
        return status;
      }
    }
    // Return null if no match is found
    return null;
  }

  // Function to get the status as String from score as Number
  private getScore(status: string): number {
    return this.statusToScore.get(status);
  }

  /**
   * Filters the crop based on the data status of each activityGroup
   *
   * The method checks the status of each ActivityGroup ( "defending", "feeding",
   * "ground_processing", "harvesting", and "seeding") in the crop passed;
   *
   * The method compares these statuses to the ones selected in the filters.
   * If any of the activityGroup in the crop object have a status that matches one of the
   * selected statuses, that crop object is accepted and the method return true.
   * @param {any} crop Each Crop inside a single DataTable item
   * @returns {boolean} True if at least one activityGroup has a status that matches one of those statuses selected in the filters
   */
  filterCropByActivityStatus(
    crop: SingleCropActivityDataStatus,
    filters: ActivitiesTableFilters
  ): boolean {
    const selectedStatuses = filters.dataStatus.map((status) => status.id);

    let isCropAccepted = false;

    for (const activityGroup of Object.values(ActivitiesGroupType)) {
      // Get the status for the current activityGroup
      const status = crop.activitiesGroupDStatus[activityGroup];

      // Check if the status matches any of the selectedStatuses
      if (selectedStatuses.includes(status)) {
        isCropAccepted = true; // Mark the crop for inclusion if there's a match
      }
    }

    // return whether the crop should be included or not
    return isCropAccepted;
  }
}
